import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImagePlus, Play } from "lucide-react";
import { Reveal, Overline, EASE } from "./shared";

const GROUPS = [
  { id: "nettoyage", label: "Nettoyage & remise en état" },
  { id: "travaux", label: "Entretien & petits travaux" },
  { id: "exterieurs", label: "Extérieurs" },
];

const FILTERS = [{ id: "tout", label: "Tout" }, ...GROUPS];

const G = "/assets/gallery";

const TILES = [
  { group: "nettoyage", cat: "Avant / Après", label: "Toiture vitrée — nettoyage", duo: true, before: `${G}/toiture-vitree-avant-nettoyage-valais.webp`, after: `${G}/toiture-vitree-apres-nettoyage-valais.webp` },
  { group: "nettoyage", cat: "Avant / Après", label: "Mur en pierre — nettoyage & rejointoiement", duo: true, before: `${G}/mur-pierre-grillage-avant-nettoyage-valais.webp`, after: `${G}/mur-pierre-grillage-apres-nettoyage-valais.webp` },
  { group: "nettoyage", cat: "Avant / Après", label: "Terrasse en pierre — nettoyage haute pression", duo: true, before: `${G}/terrasse-pierre-avant-nettoyage-haute-pression.webp`, after: `${G}/terrasse-pierre-apres-nettoyage-haute-pression.webp` },
  { group: "nettoyage", cat: "Avant / Après", label: "Sol carrelé — décapage à la monobrosse", duo: true, before: `${G}/sol-carrele-avant-decapage-monobrosse.webp`, after: `${G}/sol-carrele-apres-decapage-monobrosse.webp` },
  { group: "nettoyage", cat: "Entretien d'immeubles", label: "Monobrosse — nettoyage en profondeur", img: `${G}/monobrosse-nettoyage-sol-hall-immeuble.webp`, alt: "Décapage d'un sol carrelé à la monobrosse dans un hall d'immeuble en Valais" },
  { group: "nettoyage", cat: "Entretien d'immeubles", label: "Hall d'immeuble — intervention en cours", img: `${G}/nettoyage-monobrosse-intervention-hall-valais.webp`, alt: "Steve Rosset nettoie le sol carrelé d'un hall d'immeuble à la monobrosse" },
  { group: "nettoyage", cat: "Entretien d'immeubles", label: "Nettoyage de façade en nacelle", img: `${G}/nettoyage-facade-nacelle-sion.webp`, alt: "Nettoyage d'une façade vitrée en nacelle sur un immeuble à Sion" },
  { group: "nettoyage", cat: "Nettoyage haute pression", label: "Toiture vitrée — nettoyage haute pression", img: `${G}/toiture-vitree-karcher-haute-pression-valais.webp`, alt: "Nettoyage haute pression d'une toiture vitrée en Valais par Steve Rosset" },
  { group: "nettoyage", cat: "Nettoyage de garages", label: "Garage souterrain — nettoyage haute pression", img: `${G}/rampe-garage-avant-nettoyage-haute-pression.webp`, alt: "Rampe d'accès et murs d'un garage souterrain avant nettoyage haute pression en Valais" },
  { group: "nettoyage", cat: "Nettoyage de garages", label: "Nettoyage haute pression en action — vidéo", video: `${G}/nettoyage-haute-pression-garage-video.mp4`, videoWebm: `${G}/nettoyage-haute-pression-garage-video.webm`, poster: `${G}/nettoyage-haute-pression-garage-video-poster.webp`, alt: "Vidéo du nettoyage haute pression des murs d'un garage souterrain en Valais" },
  { group: "travaux", cat: "Avant / Après", label: "Porte d'entrée — rénovation", duo: true, before: `${G}/porte-entree-avant-renovation.webp`, after: `${G}/porte-entree-apres-renovation.webp` },
  { group: "travaux", cat: "Avant / Après", label: "Cave aménagée — rénovation complète", duo: true, before: `${G}/cave-avant-renovation-valais.webp`, after: `${G}/cave-apres-renovation-lambris-carrelage.webp` },
  { group: "travaux", cat: "Avant / Après", label: "Cloison — pose et finitions", duo: true, before: `${G}/cloison-avant-ossature-bois.webp`, after: `${G}/cloison-apres-finitions-sion.webp` },
  { group: "travaux", cat: "Rénovation", label: "Pose de cloisons", img: `${G}/pose-cloisons-renovation-sion.webp`, alt: "Pose de cloisons lors d'une rénovation intérieure à Sion" },
  { group: "travaux", cat: "Travaux d'entretien", label: "Auvent en bois sur mesure", img: `${G}/auvent-bois-sur-mesure-valais.webp`, alt: "Construction d'un auvent en bois sur mesure contre un mur en pierre" },
  { group: "travaux", cat: "Travaux d'entretien", label: "Cabanon en bois — toiture tôle", img: `${G}/cabanon-bois-toiture-tole-valais.webp`, alt: "Toiture en tôle ondulée d'un cabanon en bois réalisé sur mesure" },
  { group: "travaux", cat: "Rénovation", label: "Sol carrelé — remise à neuf", img: `${G}/sol-carrele-remise-a-neuf-sion.webp`, alt: "Sol carrelé remis à neuf lors de travaux de rénovation à Sion" },
  { group: "travaux", cat: "Rénovation", label: "Cave aménagée — finitions", img: `${G}/cave-amenagee-finitions-lambris-carrelage.webp`, alt: "Cave aménagée avec lambris bois et carrelage, finitions soignées" },
  { group: "exterieurs", cat: "Avant / Après", label: "Sentier & escalier en pierre — aménagement", duo: true, before: `${G}/sentier-avant-amenagement-exterieur-valais.webp`, after: `${G}/escalier-pierre-apres-amenagement-valais.webp` },
  { group: "exterieurs", cat: "Extérieurs", label: "Escalier et dallage en pierre — finitions", img: `${G}/escalier-dallage-pierre-apres-valais.webp`, alt: "Finitions d'un escalier et d'un dallage en pierre en Valais" },
  { group: "exterieurs", cat: "Extérieurs", label: "Pose de clôture et grillage", img: `${G}/pose-cloture-grillage-valais.webp`, alt: "Pose d'une clôture et d'un grillage sur un terrain en Valais" },
  { group: "exterieurs", cat: "Extérieurs", label: "Bordure en pierres sèches", img: `${G}/bordure-pierres-seches-paves-valais.webp`, alt: "Bordure en pierres sèches le long d'un pavé extérieur en Valais" },
  { group: "exterieurs", cat: "Extérieurs", label: "Terrasse — coffrage et dalle béton", img: `${G}/terrasse-coffrage-dalle-beton-valais.webp`, alt: "Coffrage et coulage d'une dalle béton pour une terrasse en Valais" },
  { group: "exterieurs", cat: "Extérieurs", label: "Dallage en pierre naturelle — pose", img: `${G}/dallage-pierre-naturelle-pose-valais.webp`, alt: "Pose d'un dallage en pierre naturelle sur un sentier extérieur" },
];

const Half = ({ src, tag, label }) => (
  <div className="relative flex-1 overflow-hidden">
    {src ? (
      <img src={src} alt={`${label} — ${tag.toLowerCase()}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : (
      <div className="flex h-full items-center justify-center border border-dashed border-nuit/20 bg-brume/60">
        <span className="font-display text-stroke-nuit text-3xl font-bold uppercase tracking-widest sm:text-5xl">{tag}</span>
      </div>
    )}
    <span className="absolute left-3 top-3 bg-nuit/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-lagoon">{tag}</span>
  </div>
);

const VideoTile = ({ tile }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  return (
    <div
      className="group relative aspect-[4/5] cursor-pointer overflow-hidden bg-nuit"
      onClick={toggle}
      onMouseEnter={() => { const v = ref.current; if (v) { v.play(); setPlaying(true); } }}
      onMouseLeave={() => { const v = ref.current; if (v) { v.pause(); setPlaying(false); } }}
      data-testid="video-tile-karcher"
      role="button"
      aria-label={`Lire la vidéo : ${tile.label}`}
    >
      <video ref={ref} poster={tile.poster} muted loop playsInline preload="none" className="h-full w-full object-cover" aria-label={tile.alt}>
        <source src={tile.videoWebm} type="video/webm" />
        <source src={tile.video} type="video/mp4" />
      </video>
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-nuit/70 backdrop-blur transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-0.5 h-6 w-6 text-lagoon" />
          </span>
        </div>
      )}
      <span className="absolute left-3 top-3 bg-nuit/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-lagoon">Vidéo</span>
      <div className="absolute bottom-0 left-0 bg-nuit px-4 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-lagoon">{tile.cat}</span>
        <p className="text-xs font-medium text-white">{tile.label}</p>
      </div>
    </div>
  );
};

const Tile = ({ tile }) => {
  if (tile.video) return <VideoTile tile={tile} />;
  return (
  <div className={`group relative overflow-hidden bg-brume/60 ${tile.duo ? "aspect-[2/1]" : "aspect-[4/5]"}`}>
    {tile.duo ? (
      <div className="absolute inset-0 flex">
        <Half src={tile.before} tag="Avant" label={tile.label} />
        <div className="w-0.5 bg-white" />
        <Half src={tile.after} tag="Après" label={tile.label} />
      </div>
    ) : tile.img ? (
      <img src={tile.img} alt={tile.alt || tile.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : (
      <div className="flex h-full flex-col items-center justify-center gap-3 border border-dashed border-nuit/20 transition-colors duration-300 group-hover:border-lagoon">
        <ImagePlus className="h-8 w-8 text-nuit/20 transition-colors duration-300 group-hover:text-lagoon" />
      </div>
    )}
    <div className="absolute bottom-0 left-0 bg-nuit px-4 py-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-lagoon">{tile.cat}</span>
      <p className="text-xs font-medium text-white">{tile.label}</p>
    </div>
  </div>
  );
};

export const Realisations = () => {
  const [filter, setFilter] = useState("tout");
  const shownGroups = filter === "tout" ? GROUPS : GROUPS.filter((g) => g.id === filter);

  return (
    <section id="realisations" className="bg-brume py-24 lg:py-36" data-testid="realisations-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <Overline>Réalisations</Overline>
          <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium leading-tight tracking-tight text-nuit sm:text-5xl">
            Le travail parle de lui-même
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-nuit/60">
            Une galerie qui s'enrichit au fil des interventions, organisée en trois univers :
            nettoyage et remise en état, entretien et petits travaux, extérieurs.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label="Filtrer les réalisations">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-all duration-300 ${
                  filter === f.id ? "bg-nuit text-white" : "bg-white text-nuit/60 hover:bg-nuit/5 hover:text-nuit"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        {shownGroups.map((g) => {
          const tiles = TILES.filter((t) => t.group === g.id);
          return (
            <div key={g.id} className="mt-14 first:mt-12" data-testid={`gallery-group-${g.id}`}>
              <Reveal>
                <div className="flex items-center gap-5">
                  <h3 className="font-display whitespace-nowrap text-xl font-medium text-nuit sm:text-2xl">{g.label}</h3>
                  <span className="h-px flex-1 bg-nuit/10" />
                  <span className="whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.2em] text-nuit/35">
                    {tiles.length} réalisations
                  </span>
                </div>
              </Reveal>
              <motion.div layout className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <AnimatePresence mode="popLayout">
                  {tiles.map((t) => (
                    <motion.div
                      layout
                      key={t.label}
                      initial={{ opacity: 0, scale: 0.94 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.94 }}
                      transition={{ duration: 0.45, ease: EASE }}
                      className={t.duo ? "sm:col-span-2" : ""}
                    >
                      <Tile tile={t} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
